import { Settings } from '@streamr/tv-engine/~/settings';
import { addMediaStreamTrackStopListener } from '../../utils/addMediaStreamTrackStopListener';
import { logGapDiagnosticSampled } from './GapDiagnostics';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
import * as audioRecorderProcessor from './webaudiorecorder.worklet';
export class AudioFrameExtractor {
    static LOG_PREFIX = '[AudioFrameExtractor]';
    static initialized = false;
    static moduleLoaded = false;
    static audioContext;
    // Pre-warmed extractor: created at init() time with a running worklet
    // but no track or SAB handle. Claimed by the first addMediaStreamAudioTrack().
    static preWarmedExtractor;
    track;
    trackId;
    audioSource;
    audioWorklet;
    frameCounter = 0;
    isStopped = false;
    removeTrackStopListener;
    timestampOffsetMicros;
    outputTrack;
    sabHandle;
    cumulativeSampleCount = 0;
    constructor(track, sabHandle) {
        this.track = track;
        this.trackId = track?.id ?? 'pre-warm';
        this.outputTrack = new Track(this.trackId, true);
        this.sabHandle = sabHandle;
    }
    /**
     * Pre-register the AudioWorklet processor module on a context.
     */
    static async preloadModule(audioContext) {
        if (AudioFrameExtractor.moduleLoaded)
            return;
        await audioContext.audioWorklet.addModule(
        // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
        audioRecorderProcessor);
        AudioFrameExtractor.moduleLoaded = true;
    }
    /**
     * Initialize with an externally-owned AudioContext.
     */
    static async initialize(audioContext) {
        if (AudioFrameExtractor.initialized)
            return;
        AudioFrameExtractor.audioContext = audioContext;
        if (!AudioFrameExtractor.moduleLoaded) {
            await audioContext.audioWorklet.addModule(
            // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
            audioRecorderProcessor);
            AudioFrameExtractor.moduleLoaded = true;
        }
        AudioFrameExtractor.initialized = true;
    }
    /**
     * Pre-warm a worklet on the AudioContext so its process() loop is
     * already active when the first mic track arrives. Call at engine
     * init time, after initialize().
     *
     * The pre-warmed extractor is claimed by claimPreWarmed(). Only
     * ONE worklet is created — reusing the same AudioWorkletNode
     * avoids the WebKit audio session reconfiguration freeze that
     * happens when a new node is created.
     */
    static preWarmWorklet() {
        if (AudioFrameExtractor.preWarmedExtractor)
            return;
        if (!AudioFrameExtractor.initialized) {
            console.warn(`${AudioFrameExtractor.LOG_PREFIX} preWarmWorklet: not yet initialized, skipping`);
            return;
        }
        const extractor = new AudioFrameExtractor();
        extractor.prepareWorklet();
        AudioFrameExtractor.preWarmedExtractor = extractor;
        console.log(`${AudioFrameExtractor.LOG_PREFIX} preWarmWorklet: worklet running on AudioContext`);
    }
    /**
     * Claim the pre-warmed extractor for a specific track.
     * Returns the extractor if available, otherwise undefined.
     */
    static claimPreWarmed() {
        const extractor = AudioFrameExtractor.preWarmedExtractor;
        AudioFrameExtractor.preWarmedExtractor = undefined;
        return extractor;
    }
    /**
     * Bind a mic track and (optionally) a SAB handle to a pre-warmed
     * extractor. The SAB handle is sent to the already-running worklet
     * via port.postMessage. Returns a promise that resolves once the
     * worklet confirms it has connected the SAB port.
     */
    bindTrack(track, sabHandle) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
        this.sabHandle = sabHandle;
        const worklet = this.audioWorklet;
        if (sabHandle && worklet) {
            return new Promise((resolve) => {
                const prevHandler = worklet.port.onmessage;
                worklet.port.onmessage = (event) => {
                    if (event.data?.__diag &&
                        event.data.kind === 'sabConnected') {
                        console.log(`${AudioFrameExtractor.LOG_PREFIX} bindTrack: SAB connected in worklet`, { trackId: track.id.slice(0, 8) });
                        worklet.port.onmessage = prevHandler;
                        resolve();
                        return;
                    }
                    if (prevHandler) {
                        prevHandler.call(worklet.port, event);
                    }
                };
                worklet.port.postMessage({
                    type: 'connectSab',
                    sabHandle,
                });
            });
        }
        return Promise.resolve();
    }
    /**
     * Reset the initialized state.
     */
    static async teardown() {
        if (AudioFrameExtractor.preWarmedExtractor) {
            await AudioFrameExtractor.preWarmedExtractor.stop();
            AudioFrameExtractor.preWarmedExtractor = undefined;
        }
        if (AudioFrameExtractor.audioContext) {
            console.log(`${AudioFrameExtractor.LOG_PREFIX} teardown: releasing shared AudioContext reference`);
            AudioFrameExtractor.audioContext = undefined;
        }
        AudioFrameExtractor.initialized = false;
        AudioFrameExtractor.moduleLoaded = false;
    }
    /**
     * Create the AudioWorkletNode and connect it to the context
     * destination, but do NOT attach the mic source yet.
     */
    prepareWorklet() {
        if (!AudioFrameExtractor.initialized ||
            !AudioFrameExtractor.audioContext) {
            throw new Error('AudioFrameExtractor not yet initialized');
        }
        if (this.audioWorklet)
            return;
        const ctx = AudioFrameExtractor.audioContext;
        this.audioWorklet = new AudioWorkletNode(ctx, 'audio-recorder-processor', this.sabHandle
            ? { processorOptions: { sabHandle: this.sabHandle } }
            : undefined);
        this.setupWorkletMessageHandler();
        this.audioWorklet.connect(ctx.destination);
        console.log(`${AudioFrameExtractor.LOG_PREFIX} prepareWorklet: worklet running (no mic source yet)`, { trackId: this.trackId.slice(0, 8) });
    }
    setupWorkletMessageHandler() {
        if (!this.audioWorklet)
            return;
        this.audioWorklet.port.onmessage = (event) => {
            if (this.isStopped)
                return;
            if (event.data?.__diag) {
                // DISABLED for duration-experiment run: spams console at
                // ~100 msg/s on Safari (every worklet quantum) and contaminates
                // the SEND-side timing measurements. Re-enable for debugging.
                // console.log(
                //     `[recorderWorklet] ${event.data.kind} ${JSON.stringify(event.data.data)}`,
                // );
                if (event.data.kind === 'frameSend') {
                    logGapDiagnosticSampled('recorderWorklet.frameSend', {
                        deltaMs: event.data.data?.deltaMs,
                    });
                }
                else if (event.data.kind === 'gap' ||
                    event.data.kind === 'gapReset') {
                    logGapDiagnosticSampled(`recorderWorklet.${event.data.kind}`, {
                        deltaMs: event.data.data?.gapMs,
                    });
                }
                return;
            }
            if (event.data?.samples) {
                if (this.sabHandle) {
                    console.warn(`${AudioFrameExtractor.LOG_PREFIX} samples leaked to main thread despite SAB handle being provided`, { trackId: this.trackId.slice(0, 8) });
                }
                const samples = event.data.samples;
                const numChannels = event.data.numChannels ?? 1;
                const framesPerChannel = samples.length / numChannels;
                const sampleRate = Settings.audioSampleRate;
                const timestampMicros = Math.round((this.cumulativeSampleCount / sampleRate) * 1_000_000);
                const durationMicros = Math.round((framesPerChannel / sampleRate) * 1_000_000);
                this.cumulativeSampleCount += framesPerChannel;
                const frame = this.createUnencodedAudioFrame(timestampMicros, durationMicros, samples);
                this.outputTrack.emit(frame, false, [frame.data.buffer]);
            }
        };
    }
    /**
     * Attach the mic MediaStreamTrack to the already-running worklet.
     */
    start() {
        if (!AudioFrameExtractor.initialized ||
            !AudioFrameExtractor.audioContext) {
            throw new Error('AudioFrameExtractor not yet initialized');
        }
        if (!this.track) {
            throw new Error('AudioFrameExtractor: no track bound — call bindTrack() first');
        }
        if (!this.audioWorklet) {
            this.prepareWorklet();
        }
        const ctx = AudioFrameExtractor.audioContext;
        this.audioSource = ctx.createMediaStreamSource(new MediaStream([this.track]));
        this.audioSource.connect(this.audioWorklet);
        console.log(`${AudioFrameExtractor.LOG_PREFIX} start: mic source attached to running worklet`, { trackId: this.trackId.slice(0, 8) });
        this.removeTrackStopListener = addMediaStreamTrackStopListener(this.track, () => {
            console.log('AudioFrameExtractor: track stopped/ended');
            this.cleanup();
        });
    }
    cleanup() {
        if (this.isStopped)
            return;
        this.isStopped = true;
        console.log('AudioFrameExtractor: cleaning up');
        if (this.audioSource) {
            this.audioSource.disconnect();
            this.audioSource = undefined;
        }
        if (this.audioWorklet) {
            this.audioWorklet.port.onmessage = null;
            this.audioWorklet.disconnect();
            this.audioWorklet = undefined;
        }
        if (this.removeTrackStopListener) {
            this.removeTrackStopListener();
            this.removeTrackStopListener = undefined;
        }
        this.outputTrack.emit(undefined, true);
        this.outputTrack.unsetListener();
        console.log('AudioFrameExtractor: cleanup complete');
    }
    createUnencodedAudioFrame(contentTimestampMicros, durationMicros, data) {
        if (this.timestampOffsetMicros === undefined) {
            this.timestampOffsetMicros = Timekeeper.getInstance('producer:individual').getLastTimestampMicros();
        }
        const timestampMicros = BigInt(contentTimestampMicros) + this.timestampOffsetMicros;
        return {
            frameNumber: BigInt(this.frameCounter++),
            timestamp: timestampMicros,
            duration: BigInt(durationMicros),
            trackId: this.trackId ?? '',
            data: data,
            done: false,
            isKeyFrame: true,
            producerWallMillis: Date.now(),
        };
    }
    stop() {
        if (this.isStopped) {
            return Promise.resolve();
        }
        console.log('AudioFrameExtractor: stop() called');
        this.cleanup();
        return Promise.resolve();
    }
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
    getAudioWorkletNode() {
        return this.audioWorklet;
    }
}
